import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Lock, Mail } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { formatApiErrorDetail } from '@/lib/api';
import { UKGMLogo } from '@/lib/ui';

export default function AdminLogin() {
  const { t } = useTranslation();
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  if (user && user.role === 'admin') return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u.role !== 'admin') toast.error('Admin access required');
      else { toast.success('Welcome back'); nav('/admin'); }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Login failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-ukgm-cream flex items-center justify-center p-4 mandala-bg" data-testid="admin-login-page">
      <div className="w-full max-w-md bg-white rounded-2xl border border-ukgm-gold/30 shadow-xl overflow-hidden">
        <div className="bg-ukgm-blue text-white p-6 text-center">
          <div className="flex justify-center"><UKGMLogo className="w-20 h-20" /></div>
          <div className="mt-4 font-serif text-lg">Universal Krishna Gaukul Mission</div>
          <p className="text-[10px] text-ukgm-gold/85 tracking-widest uppercase mt-1">A Registered Public Charitable Trust in India</p>
          <div className="mt-4 gold-divider" />
          <h1 className="mt-4 font-serif text-xl">{t('admin.login_title')}</h1>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <label className="block">
            <div className="text-xs tracking-widest uppercase text-ukgm-slate font-bold">{t('admin.email')}</div>
            <div className="mt-2 relative">
              <Mail className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-ukgm-gold" />
              <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full pl-10 pr-4 py-3 rounded-full bg-ukgm-cream text-sm" data-testid="admin-email-input" />
            </div>
          </label>
          <label className="block">
            <div className="text-xs tracking-widest uppercase text-ukgm-slate font-bold">{t('admin.password')}</div>
            <div className="mt-2 relative">
              <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-ukgm-gold" />
              <input type="password" required value={password} onChange={e => setPassword(e.target.value)} className="w-full pl-10 pr-4 py-3 rounded-full bg-ukgm-cream text-sm" data-testid="admin-password-input" />
            </div>
          </label>
          <button disabled={loading} className="w-full bg-ukgm-gold text-ukgm-blue font-bold px-6 py-3 rounded-full hover:bg-ukgm-goldDark disabled:opacity-50" data-testid="admin-signin-btn">
            {loading ? t('admin.signing_in') : t('admin.signin')}
          </button>
        </form>
      </div>
    </div>
  );
}
